export const superAdminApi = (client) => ({
    /** Every org the super admin can switch into, with admin email + user count. */
    listAllOrgs: () => client.get('/api/v1/super-admin/orgs').then((r) => r.data),
    /** Single-org rich detail with full counts + admin user. */
    getOrgDetail: (slug) => client.get(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}`).then((r) => r.data),
    /** The full end-to-end org report as a PDF blob — routed through the
     *  normal authenticated client since a plain `<a href>` wouldn't carry
     *  the Authorization header; the caller turns this into an object URL
     *  to trigger the browser download. */
    downloadOrgReportPdf: (slug) => client.get(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}/report`, { params: { format: 'pdf' }, responseType: 'blob' }).then((r) => r.data),
    /** Rename an org (slug stays the same). */
    renameOrg: (slug, name) => client.patch(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}`, { name }).then((r) => r.data),
    /** Hard delete an org. Optionally email the admin a reason first. */
    deleteOrg: (slug, body) => client.delete(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}`, { data: body ?? {} }).then((r) => r.data),
    /** Email the admin (or every user) a custom message. */
    notifyOrg: (slug, body) => client.post(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}/notify`, body).then((r) => r.data),
    // ─── Per-org user management ─────────────────────────────────────────
    listOrgUsers: (slug) => client.get(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}/users`).then((r) => r.data),
    listOrgRoles: (slug) => client.get(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}/roles`).then((r) => r.data),
    updateOrgUserRole: (slug, userId, role) => client.patch(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}/users/${userId}/role`, { role }).then((r) => r.data),
    deactivateOrgUser: (slug, userId) => client.post(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}/users/${userId}/deactivate`, {}).then((r) => r.data),
    reactivateOrgUser: (slug, userId) => client.post(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}/users/${userId}/reactivate`, {}).then((r) => r.data),
    removeOrgUser: (slug, userId) => client.delete(`/api/v1/super-admin/orgs/slug/${encodeURIComponent(slug)}/users/${userId}`).then((r) => r.data),
    /** Signup approval queue — visible only to super admins. */
    listInviteRequests: (status) => client
        .get('/api/v1/super-admin/invite-requests', {
        params: status ? { status } : {},
    })
        .then((r) => r.data),
    approveInviteRequest: (id) => client
        .post(`/api/v1/super-admin/invite-requests/${id}/approve`)
        .then((r) => r.data),
    declineInviteRequest: (id, body) => client
        .post(`/api/v1/super-admin/invite-requests/${id}/decline`, body ?? {})
        .then((r) => r.data),
    resendInviteCode: (id) => client
        .post(`/api/v1/super-admin/invite-requests/${id}/resend-code`, {})
        .then((r) => r.data),
    // ─── Cross-org user invitations ──────────────────────────────────────
    /** Every pending org-admin user invitation across every workspace. */
    listAllInvitations: () => client
        .get('/api/v1/super-admin/invitations')
        .then((r) => r.data),
    /** Revoke a stuck invitation so the org admin can re-invite. */
    revokeInvitationInOrg: (slug, id) => client
        .delete(`/api/v1/super-admin/invitations/${encodeURIComponent(slug)}/${id}`)
        .then((r) => r.data),
    // ─── Cross-org feedback ────────────────────────────────────────────
    /** Every Help & Support submission across every workspace, sorted
     *  by most recent thread activity. */
    listAllFeedback: () => client
        .get('/api/v1/super-admin/feedback')
        .then((r) => r.data),
    /** Move a submission through Submitted → Acknowledged → Resolved
     *  with no reply attached. Use `sendFeedbackMessage` to reply and
     *  optionally change status in one call. */
    updateFeedbackStatus: (slug, id, body) => client
        .patch(`/api/v1/super-admin/feedback/${encodeURIComponent(slug)}/${id}`, body)
        .then((r) => r.data),
    /** Reply thread on a submission in a specific workspace. */
    listFeedbackMessages: (slug, id) => client
        .get(`/api/v1/super-admin/feedback/${encodeURIComponent(slug)}/${id}/messages`)
        .then((r) => r.data),
    /** Post a reply, optionally bundling a status change (the "reply &
     *  mark resolved" button on the detail panel) and/or image
     *  attachments (upload each via `uploadFeedbackImage` first). */
    sendFeedbackMessage: (slug, id, body) => client
        .post(`/api/v1/super-admin/feedback/${encodeURIComponent(slug)}/${id}/messages`, body)
        .then((r) => r.data),
    /** Upload an image to attach to a feedback reply — mirrors the
     *  org-user `feedbackApi.uploadImage` flow. */
    uploadFeedbackImage: (file) => {
        const form = new FormData();
        form.append('file', file);
        return client.post('/api/v1/super-admin/feedback/upload-image', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then((r) => r.data);
    },
    /** Delete one of the super admin's own replies (soft-delete). */
    deleteFeedbackMessage: (slug, id, msgId) => client
        .delete(`/api/v1/super-admin/feedback/${encodeURIComponent(slug)}/${id}/messages/${msgId}`)
        .then((r) => r.data),
    /** Delete a submission entirely — clears it out of the triage queue. */
    deleteFeedback: (slug, id) => client
        .delete(`/api/v1/super-admin/feedback/${encodeURIComponent(slug)}/${id}`)
        .then((r) => r.data),
});
//# sourceMappingURL=super-admin.js.map